import { client } from '@api/rest/client';
import type { ConversationKeywordDTO } from '@conversed/rest/src/contracts/conversation-keywords/conversation-keywords.dto';
import ErrorCodes from '@conversed/shared/src/errors/error-codes';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useMainSelector } from '../../useMainSelector';

export const useConversationKeywordsActions = () => {
  const dispatch = useDispatch();
  const {
    conversationKeywords: { keywords },
  } = useMainSelector();

  const fetchConversationKeywords = useCallback(
    async (withoutLoadingAnimation = false) => {
      try {
        if (!withoutLoadingAnimation) {
          dispatch(mainActions.setUIState({ isLoading: true }));
        }

        const { data } =
          await client.conversationKeywords.getConversationKeywords();
        dispatch(mainActions.setConversationKeywords(data));
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        if (!withoutLoadingAnimation) {
          dispatch(mainActions.setUIState({ isLoading: false }));
        }
      }
    },
    [dispatch],
  );

  const createConversationKeyword = useCallback(
    async (keyword: string): Promise<ConversationKeywordDTO | undefined> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } = await client.conversationKeywords.createKeyword({
          body: { keyword },
        });

        toastSuccessTranslated('conversation-keywords.create.success');

        // Append the new keyword to the current list in alphabetical order
        const updatedKeywords = [...keywords, data].sort(
          (firstKeyword, secondKeyword) =>
            firstKeyword.keyword.localeCompare(secondKeyword.keyword),
        );
        dispatch(mainActions.setConversationKeywords(updatedKeywords));

        return data;
      } catch (error: any) {
        console.error(error);

        if (error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS) {
          toastErrorTranslated(
            'conversation-keywords.create.duplicate-name-toast-message',
            { keyword },
          );
        } else {
          toastErrorTranslated(error);
        }

        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch, keywords],
  );

  const updateConversationKeyword = useCallback(
    async (id: string, keyword: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } = await client.conversationKeywords.editKeyword({
          params: { id },
          body: { keyword },
        });

        toastSuccessTranslated('conversation-keywords.update.success');
        dispatch(mainActions.updateConversationKeyword(data));

        return data;
      } catch (error: any) {
        console.error(error);

        if (error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS) {
          toastErrorTranslated(
            'conversation-keywords.create.duplicate-name-toast-message',
            { keyword },
          );
        } else {
          toastErrorTranslated(error);
        }

        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const deleteConversationKeyword = useCallback(
    async (id: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        await client.conversationKeywords.deleteKeyword({
          params: { id },
          body: undefined,
        });

        toastSuccessTranslated('conversation-keywords.delete.success');
        dispatch(mainActions.deleteConversationKeyword(id));
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  // Assigns keywords to a specific conversation and upserts any new ones
  // into the company keyword pool on the backend.
  const updateConversationKeywords = useCallback(
    async (conversationId: string, keywordsToAssign: string[]) => {
      try {
        const { data } =
          await client.conversationKeywords.updateConversationKeywords({
            params: { conversationId },
            body: { keywords: keywordsToAssign },
          });
        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [],
  );

  return {
    fetchConversationKeywords,
    createConversationKeyword,
    updateConversationKeyword,
    deleteConversationKeyword,
    updateConversationKeywords,
  };
};
